import { createContext, useCallback, useContext, useState } from 'react';

/**
 * The FC Dashboard speaks Korean or English (SSD-7602). Names come from the daily Fulfillment Report the center already
 * reads (its Korean wording and the English it pairs with each phrase), so a number is called the same thing in both.
 * Where the report has no word for a number, the wording below is this screen's own.
 */
export type FcdLang = 'ko' | 'en';

const STORAGE_KEY = 'fcd_lang';

const readStored = (): FcdLang | null => {
  try {
    const value = typeof window === 'undefined' ? null : window.localStorage.getItem(STORAGE_KEY);
    return value === 'ko' || value === 'en' ? value : null;
  } catch {
    return null;
  }
};

/** The language this browser chose last; Korean, the report's own, until one is chosen. */
export const useFcdLangState = (): [FcdLang, (next: FcdLang) => void] => {
  const [lang, setLang] = useState<FcdLang>(() => readStored() ?? 'ko');
  const choose = useCallback((next: FcdLang) => {
    setLang(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A browser that refuses storage still switches; it just forgets on reload.
    }
  }, []);
  return [lang, choose];
};

const count = (value: number): string => value.toLocaleString('en-US');

const ko = {
  langGroup: '언어',
  tabs: { today: '오늘', logistics: '물류 리포트', monthly: '먼슬리' },
  tablist: '대시보드',

  // Today: the bar.
  day: '기준일',
  dayInput: '처리량을 셀 날짜 (KST)',
  yesterday: '어제',
  refresh: '새로고침',
  reading: '읽는 중…',
  tryAgain: '다시 읽기',
  readFailed: '대시보드를 읽지 못했어요.',
  asOf: (time: string) => `${time} 기준 스냅샷`,
  everyQuarter: '이 탭이 열려 있으면 15분마다 다시 읽어요',
  calendarChip: '달력일 · 한국시간',
  laterReadFailed: (at: string, message: string, generatedAt: string, date: string) =>
    `${at}에 다시 읽다가 실패했어요: ${message} 아래 숫자는 ${generatedAt}에 ${date} 기준으로 읽은 값이에요.`,

  // Today: the groups and tiles.
  throughput: (day: string) => `일별 처리량 · ${day}`,
  throughputNote: '그날 KST 하루 동안 찍힌 기록',
  waitingNow: '지금 기다리는 오더',
  waitingNote: (time: string) => `${time} 기준`,
  inbound: '입고 (제품)',
  outbound: '아웃바운드 (주문)',
  handover: '인계',
  ready: '입고 끝 — 포장만 남음',
  // Not the report's wording on purpose: the report's 「제품은 다 왔는데 입고가 안 끝남」 counts orders with every item
  // delivered and its Order Team list counts line items, while this tile counts every AWAITING_INBOUND order.
  awaiting: '입고가 안 끝난 오더',
  readyQualifier: '주문한 지 2일(48시간) 넘음',
  awaitingQualifier: '주문한 지 3일(72시간) 넘음',
  units: '개',
  orders: '오더',
  boxes: '박스',
  inboundSub: (orders: number, cancelled: number) => `${count(orders)}오더 · 입고 취소 ${count(cancelled)}개`,
  inboundRequests: (units: number, requests: number) =>
    `고객 주문 입고에서 뺀 입고 ${count(units)}개 · 입고 요청 ${count(requests)}건`,
  outboundSub: (units: number, cancelled: number) => `${count(units)}개 · 아웃바운드 취소 ${count(cancelled)}개`,
  handoverSub: '송장 1장 = 1박스',
  handoverCancelled: (boxes: number) => `그중 ${count(boxes)}박스는 취소된 마감에만 있어요`,
  readySub: (onHold: number, marketing: number) => `보류 ${count(onHold)} · 마케팅 ${count(marketing)}`,
  awaitingSub: (customer: number, marketing: number) => `고객 ${count(customer)} · 마케팅 ${count(marketing)}`,
  readyBasis: '상태 READY · 아웃바운드 전',
  awaitingBasis: '상태 AWAITING_INBOUND',
  viewList: '목록 보기',
  inboundBasis: '입고 스캔 − 입고 취소 · 고객 주문',
  outboundBasis: '아웃바운드 스캔 − 아웃바운드 취소',
  handoverBasis: '일일 마감에서 스캔한 송장',
  couldNotRead: (error: string) => `읽지 못했어요: ${error}`,

  // Today: the waiting list.
  waitingList: '기다리는 오더 · 오래된 순',
  listChoice: '목록',
  oldestOf: (shown: number, total: number) => `오래된 ${count(shown)}오더 · 전체 ${count(total)}오더`,
  readyEmpty: '48시간 넘게 포장만 남은 오더가 없어요.',
  awaitingEmpty: '72시간 넘게 입고를 기다리는 오더가 없어요.',
  colOrder: '주문',
  colPlaced: '주문 시각',
  colSince: '주문한 지',
  colPic: '담당(PIC)',
  colStatus: '상태',
  onHold: '보류',
  marketing: '마케팅',
  waited: (days: number, hours: number) => (days > 0 ? `${days}일 ${hours}시간` : `${hours}시간`),

  // Today: the headed comments.
  headed: (month: string) => `머리말 코멘트 · ${month}`,
  headedThisMonth: '머리말 코멘트 · 이번 달',
  headedTotal: '전체',
  headedRule: '같은 주문의 같은 머리말은 72시간 안에 1건으로 세요.',
  headedShortUnplaced: (unplaced: number) => `덜 셌을 수 있어요: 시각을 못 읽은 머리말 코멘트 ${count(unplaced)}건`,
  headedShortSearch: '덜 셌을 수 있어요: 코멘트 검색이 달 초까지 닿지 못했어요.',
  headedEmpty: '이번 달 머리말 코멘트가 아직 없어요.',
  colRef: '주문 / 입고 요청',
  colWhen: '시각',
  colHeaders: '머리말',
  colBy: '작성자',
  colComment: '코멘트',
  header: {
    UNRECOGNIZED: '[미인식]',
    INBOUND_ANOMALY: '[입고이상]',
    REORDER: '[재발주]',
    STOCK_MISMATCH: '[재고불일치]',
  },

  // Fulfillment Report tab: a copy of the Kali-server report page, as the team sees it (SSD-7638).
  reportEmpty: '아직 회사 서버에 받은 리포트 복사본이 없어요. 첫 회차가 들어오면 리포트가 여기에 그대로 보여요.',
  reportFailed: '리포트 복사본을 읽지 못했어요',
  reportReading: '리포트 복사본을 읽는 중',
  reportBar: '리포트 회차',
  reportRunAt: (at: string) => `${at} 회차`,
  reportReceived: (at: string) => `${at} 받음`,
  reportKind: { full: '아침 전체', light: '20분 갱신', close: '마감' } as Record<'full' | 'light' | 'close', string>,
  reportStale: (age: string) =>
    `마지막 회차가 ${age} 전이에요. 리포트 시간(08:00~20:00)에는 20분마다 새 회차가 와야 해요.`,
  reportFrame: '물류 리포트 복사본',
  reportCopyNote: '리포트 서버가 보낸 복사본이에요. 출고 완료(테스트용) 버튼은 복사본에서 빠져 있어요.',
  logisticsList: '물류 리포트 블록',

  // Monthly tab.
  monthlyTitle: '먼슬리 리뷰',
  monthlyIntro:
    '노션 Monthly Meetings 페이지의 칸을 같은 순서로 모두 옮겼어요. 숫자는 아직 연결 전이고, 칸마다 어떻게 채우는지 적었어요.',
  monthlyRows: (rows: number) => `${count(rows)}칸`,
  monthlyStatRows: '전체 칸',
  monthlyStatWired: '숫자 연결',
  monthlyStatLocked: '권한 대기',
  monthlyFilter: '채우는 방법으로 거르기',
  monthlyAll: '전체',
  monthlySections: '섹션',
  monthlyColRow: '칸',
  monthlyColFill: '채우는 방법',
  monthlyColSource: '출처',
  monthlyColValue: '이번 달',
  monthlyLockedNote: 'finance:read 권한이 정해지면 값을 보여 줘요',
  monthlyLockedChip: '잠김',
  monthlyNoMatch: '이 방법으로 채우는 칸이 없어요.',
  wip: 'WIP',
};

export type FcdText = typeof ko;

const en: FcdText = {
  langGroup: 'Language',
  tabs: { today: 'Today', logistics: 'Fulfillment Report', monthly: 'Monthly' },
  tablist: 'Dashboard',

  day: 'Day',
  dayInput: 'Day the throughput counts (KST)',
  yesterday: 'Yesterday',
  refresh: 'Refresh',
  reading: 'Reading…',
  tryAgain: 'Try again',
  readFailed: 'The FC dashboard could not be read.',
  asOf: (time: string) => `Snapshot as of ${time}`,
  everyQuarter: 'Reads again every 15 minutes while this tab is open',
  calendarChip: 'Calendar days · KST',
  laterReadFailed: (at: string, message: string, generatedAt: string, date: string) =>
    `The read at ${at} failed: ${message} The numbers below are from ${generatedAt} for ${date}.`,

  throughput: (day: string) => `Daily throughput · ${day}`,
  throughputNote: 'What was recorded that KST day',
  waitingNow: 'Orders waiting now',
  waitingNote: (time: string) => `as of ${time}`,
  inbound: 'Inbound (units)',
  outbound: 'Outbound (orders)',
  handover: 'Handover',
  ready: 'Inbounded — packing only',
  awaiting: 'Inbound not complete',
  readyQualifier: 'Ordered over 2 days (48h) ago',
  awaitingQualifier: 'Ordered over 3 days (72h) ago',
  units: 'units',
  orders: 'orders',
  boxes: 'boxes',
  inboundSub: (orders: number, cancelled: number) => `${count(orders)} orders · ${count(cancelled)} cancelled`,
  inboundRequests: (units: number, requests: number) =>
    `Not counted above: ${count(units)} units on ${count(requests)} inbound requests`,
  outboundSub: (units: number, cancelled: number) => `${count(units)} units · ${count(cancelled)} cancelled`,
  handoverSub: 'One waybill = one box',
  handoverCancelled: (boxes: number) => `${count(boxes)} only in a cancelled closing`,
  readySub: (onHold: number, marketing: number) => `${count(onHold)} on hold · ${count(marketing)} marketing`,
  awaitingSub: (customer: number, marketing: number) => `${count(customer)} customer · ${count(marketing)} marketing`,
  readyBasis: 'Status READY, before Outbound',
  awaitingBasis: 'Status AWAITING_INBOUND',
  viewList: 'View list',
  inboundBasis: 'Inbound scans less Inbound cancels · customer orders',
  outboundBasis: 'Outbound scans less Outbound cancels',
  handoverBasis: 'Waybills scanned at Daily Closing',
  couldNotRead: (error: string) => `Could not read: ${error}`,

  waitingList: 'Waiting orders, oldest first',
  listChoice: 'List',
  oldestOf: (shown: number, total: number) => `Oldest ${count(shown)} of ${count(total)}`,
  readyEmpty: 'No order has waited 48 hours with every item in.',
  awaitingEmpty: 'No order has waited 72 hours for its items.',
  colOrder: 'Order',
  colPlaced: 'Placed',
  colSince: 'Since order',
  colPic: 'PIC',
  colStatus: 'Status',
  onHold: 'On hold',
  marketing: 'Marketing',
  waited: (days: number, hours: number) => (days > 0 ? `${days}d ${hours}h` : `${hours}h`),

  headed: (month: string) => `Headed comments · ${month}`,
  headedThisMonth: 'Headed comments · this month',
  headedTotal: 'Total',
  headedRule: 'Same header on the same order within 72 hours counts once.',
  headedShortUnplaced: (unplaced: number) =>
    `May be short: ${count(unplaced)} headed comments have a time that could not be read.`,
  headedShortSearch: 'May be short: the comment search did not reach the start of the month.',
  headedEmpty: 'No headed comments yet this month.',
  colRef: 'Order / request',
  colWhen: 'When',
  colHeaders: 'Counted as',
  colBy: 'By',
  colComment: 'Comment',
  header: {
    UNRECOGNIZED: 'Unrecognized',
    INBOUND_ANOMALY: 'Inbound anomaly',
    REORDER: 'Reorder',
    STOCK_MISMATCH: 'Stock mismatch',
  },

  reportEmpty:
    'No copy of the Fulfillment Report has reached the company server yet. The report appears here as is once the first run arrives.',
  reportFailed: 'Could not read the report copy',
  reportReading: 'Reading the report copy',
  reportBar: 'Report run',
  reportRunAt: (at: string) => `Run ${at}`,
  reportReceived: (at: string) => `received ${at}`,
  reportKind: { full: 'Morning build', light: '20-min refresh', close: 'Close' } as Record<
    'full' | 'light' | 'close',
    string
  >,
  reportStale: (age: string) =>
    `The last run was ${age} ago. During report hours (08:00 to 20:00 KST) a new run should arrive every 20 minutes.`,
  reportFrame: 'Fulfillment Report copy',
  reportCopyNote: 'A copy sent by the report server. The test-only handover button is left out of the copy.',
  logisticsList: 'Fulfillment Report blocks',

  monthlyTitle: 'Monthly review',
  monthlyIntro:
    'Every row of the monthly review, in the order of the Notion page. Numbers are not wired in yet; each row says how it gets filled.',
  monthlyRows: (rows: number) => `${count(rows)} rows`,
  monthlyStatRows: 'Rows',
  monthlyStatWired: 'Wired in',
  monthlyStatLocked: 'Waiting for access',
  monthlyFilter: 'Filter by how rows are filled',
  monthlyAll: 'All',
  monthlySections: 'Sections',
  monthlyColRow: 'Row',
  monthlyColFill: 'How it is filled',
  monthlyColSource: 'From',
  monthlyColValue: 'This month',
  monthlyLockedNote: 'Values shown once finance:read access is decided',
  monthlyLockedChip: 'Locked',
  monthlyNoMatch: 'No row is filled this way.',
  wip: 'WIP',
};

export const FCD_TEXT: Record<FcdLang, FcdText> = { ko, en };

export const FcdLangContext = createContext<FcdLang>('ko');

/** The wording for the language chosen at the top of the page. */
export const useFcdText = (): FcdText => FCD_TEXT[useContext(FcdLangContext)];

export const useFcdLang = (): FcdLang => useContext(FcdLangContext);
